import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import AdminLayout from '@/components/AdminLayout';
import { Card } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  CalendarIcon, DollarSign, Banknote, CreditCard, 
  ArrowLeftRight, Scissors, Store, Clock, User, UserCheck
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDailyCutServices() {
  const { user, getAuthHeaders } = useAuth();
  const [date, setDate] = useState(new Date());
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('all');
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allBarbers, setAllBarbers] = useState([]);

  const isMasterAdmin = user?.role === 'master_admin';

  useEffect(() => {
    fetchBranches();
    fetchBarbers();
  }, []);

  const fetchBarbers = async () => {
    try {
      const r = await axios.get(`${API}/barbers`, getAuthHeaders());
      setAllBarbers(r.data || []);
    } catch (_e) { /* silent */ }
  };

  const resolveBarberName = (item, sale) => {
    if (item?.barber_distribution?.length > 0) {
      return item.barber_distribution
        .map((d) => allBarbers.find((b) => b.id === d.barber_id)?.name || '?')
        .join(' + ');
    }
    if (item?.barber_id) {
      const b = allBarbers.find((x) => x.id === item.barber_id);
      if (b) return b.name;
    }
    return item?.barber_name || sale?.barber_name || null;
  };

  useEffect(() => {
    fetchSales();
  }, [date, selectedBranch]);

  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API}/branches`, getAuthHeaders());
      let availableBranches = response.data;
      
      // Manager and Admin can only see their assigned branch
      if ((user?.role === 'manager' || user?.role === 'admin') && user?.branch_id) {
        availableBranches = response.data.filter(b => b.id === user.branch_id);
        setSelectedBranch(user.branch_id);
      }
      
      setBranches(availableBranches);
    } catch (error) {
      // error handled by toast
    }
  };

  const fetchSales = async () => {
    setLoading(true);
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const branchParam = selectedBranch !== 'all' ? `&branch_id=${selectedBranch}` : '';
      const response = await axios.get(
        `${API}/sales?date_from=${dateStr}T00:00:00&date_to=${dateStr}T23:59:59${branchParam}`,
        getAuthHeaders()
      );
      setSales(response.data);
    } catch (error) {
      toast.error('Error al cargar ventas');
    } finally {
      setLoading(false);
    }
  };

  // Filter only services from sales (excluding cancelled sales and cancelled items)
  const serviceItems = sales
    .filter(sale => !sale.cancelled) // Exclude cancelled sales
    .flatMap(sale => 
      sale.items
        .filter(item => item.item_type === 'service' && !item.cancelled) // Exclude cancelled items
        .map(item => ({
          ...item,
          saleId: sale.id,
          saleTime: sale.created_at,
          branchName: sale.branch_name,
          userName: sale.user_name,
          barberName: resolveBarberName(item, sale),
          paymentMethod: sale.payment_method
        }))
    );

  // Calculate totals (only from non-cancelled sales and items)
  const activeSales = sales.filter(s => !s.cancelled);
  const totals = {
    total: serviceItems.reduce((sum, item) => sum + (item.price * item.quantity), 0),
    count: serviceItems.length,
    byPayment: {
      cash: activeSales.filter(s => s.payment_method === 'cash')
        .reduce((sum, s) => sum + s.items.filter(i => i.item_type === 'service' && !i.cancelled).reduce((a, i) => a + i.price * i.quantity, 0), 0),
      card: activeSales.filter(s => s.payment_method === 'card')
        .reduce((sum, s) => sum + s.items.filter(i => i.item_type === 'service' && !i.cancelled).reduce((a, i) => a + i.price * i.quantity, 0), 0),
      transfer: activeSales.filter(s => s.payment_method === 'transfer')
        .reduce((sum, s) => sum + s.items.filter(i => i.item_type === 'service' && !i.cancelled).reduce((a, i) => a + i.price * i.quantity, 0), 0),
    }
  };

  // Group by branch
  const byBranch = branches.map(branch => {
    const branchServices = serviceItems.filter(s => {
      const sale = sales.find(sale => sale.id === s.saleId);
      return sale?.branch_id === branch.id;
    });
    return {
      branchId: branch.id,
      branchName: branch.name,
      total: branchServices.reduce((sum, item) => sum + (item.price * item.quantity), 0),
      count: branchServices.length
    };
  }).filter(b => b.count > 0);

  const getPaymentIcon = (method) => {
    switch (method) {
      case 'cash': return <Banknote className="w-4 h-4 text-green-500" />;
      case 'card': return <CreditCard className="w-4 h-4 text-blue-500" />;
      case 'transfer': return <ArrowLeftRight className="w-4 h-4 text-purple-500" />;
      default: return <DollarSign className="w-4 h-4 text-zinc-500" />;
    }
  };

  const StatCard = ({ title, value, icon: Icon, subtitle }) => (
    <Card className="bg-zinc-900/50 border-zinc-800 p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-zinc-500 uppercase tracking-widest mb-1">{title}</p>
          <p className="font-heading text-2xl text-gold">{value}</p>
          {subtitle && <p className="text-sm text-zinc-500 mt-1">{subtitle}</p>}
        </div>
        <div className="w-10 h-10 rounded-sm bg-gold/10 flex items-center justify-center">
          <Icon className="w-5 h-5 text-gold" />
        </div>
      </div>
    </Card>
  );

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-daily-cut-services">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl text-white tracking-tight flex items-center gap-3">
              <Scissors className="w-8 h-8 text-gold" />
              Corte del Día - Servicios
            </h1>
            <p className="text-zinc-500 mt-1">Solo servicios realizados</p>
          </div>
          <div className="flex gap-3">
            <Select 
              value={selectedBranch} 
              onValueChange={setSelectedBranch}
              disabled={!isMasterAdmin && user?.branch_id}
            >
              <SelectTrigger className="w-[180px] bg-zinc-900 border-zinc-800 text-white">
                <SelectValue placeholder="Sucursal" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                {isMasterAdmin && (
                  <SelectItem value="all" className="text-white">Todas las sucursales</SelectItem>
                )}
                {branches.map((branch) => (
                  <SelectItem key={branch.id} value={branch.id} className="text-white">
                    {branch.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-[180px] justify-start text-left font-normal bg-zinc-900 border-zinc-800 text-white hover:bg-zinc-800"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {format(date, 'dd MMM yyyy', { locale: es })}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800" align="end">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(d) => d && setDate(d)}
                  initialFocus
                  className="bg-zinc-900"
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-zinc-500">Cargando...</div>
        ) : (
          <>
            {/* Main Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-gold/10 border-gold/30 p-5 col-span-2">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs text-gold uppercase tracking-widest mb-1">Total Servicios</p>
                    <p className="font-heading text-4xl text-gold">${totals.total.toFixed(2)}</p>
                    <p className="text-sm text-zinc-400 mt-1">{totals.count} servicios</p>
                  </div>
                  <div className="w-12 h-12 rounded-sm bg-gold/20 flex items-center justify-center">
                    <Scissors className="w-6 h-6 text-gold" />
                  </div>
                </div>
              </Card>
              <StatCard 
                title="Efectivo" 
                value={`$${totals.byPayment.cash.toFixed(2)}`}
                icon={Banknote}
              />
              <StatCard 
                title="Tarjeta" 
                value={`$${totals.byPayment.card.toFixed(2)}`}
                icon={CreditCard}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <StatCard 
                title="Transferencia" 
                value={`$${totals.byPayment.transfer.toFixed(2)}`}
                icon={ArrowLeftRight}
              />
            </div>

            {/* By Branch */}
            {byBranch.length > 0 && selectedBranch === 'all' && (
              <Card className="bg-zinc-900/50 border-zinc-800 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Store className="w-4 h-4 text-gold" />
                  <h3 className="text-sm text-zinc-500 uppercase tracking-widest">
                    Servicios por Sucursal
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-zinc-800">
                        <th className="text-left text-xs text-zinc-500 uppercase tracking-widest pb-3">Sucursal</th>
                        <th className="text-right text-xs text-zinc-500 uppercase tracking-widest pb-3">Servicios</th>
                        <th className="text-right text-xs text-zinc-500 uppercase tracking-widest pb-3">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byBranch.map((branch) => (
                        <tr key={branch.branchId} className="border-b border-zinc-800/50 last:border-0">
                          <td className="py-3 text-white">{branch.branchName}</td>
                          <td className="py-3 text-right text-zinc-400">{branch.count}</td>
                          <td className="py-3 text-right text-gold font-semibold">${branch.total.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* Services List */}
            {serviceItems.length > 0 ? (
              <Card className="bg-zinc-900/50 border-zinc-800 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Scissors className="w-4 h-4 text-gold" />
                  <h3 className="text-sm text-zinc-500 uppercase tracking-widest">
                    Servicios Realizados ({serviceItems.length})
                  </h3>
                </div>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3 pr-4">
                    {serviceItems.map((item, idx) => (
                      <div 
                        key={`${item.saleId}-${idx}`}
                        className="bg-gold/5 border border-gold/20 rounded-lg p-4"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Scissors className="w-5 h-5 text-gold" />
                            <div>
                              <p className="text-gold font-medium">{item.name}</p>
                              <div className="flex items-center gap-3 text-xs text-zinc-500 mt-1">
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {new Date(item.saleTime).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                                </span>
                                {item.branchName && (
                                  <span className="flex items-center gap-1">
                                    <Store className="w-3 h-3" />
                                    {item.branchName}
                                  </span>
                                )}
                                {item.barberName ? (
                                  <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-gold/10 border border-gold/30 text-gold font-medium" data-testid={`service-barber-${item.saleId}`}>
                                    <UserCheck className="w-3 h-3" />
                                    {item.barberName}
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-red-500/10 border border-red-500/30 text-red-400">
                                    <UserCheck className="w-3 h-3" />
                                    Sin barbero
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {getPaymentIcon(item.paymentMethod)}
                            <span className="text-gold font-heading text-xl">
                              ${(item.price * item.quantity).toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </Card>
            ) : (
              <Card className="bg-zinc-900/50 border-zinc-800 p-12 text-center">
                <Scissors className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500">No hay servicios registrados para esta fecha</p>
              </Card>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
